import React, { useState, useEffect } from 'react';
import { useEmailAuth } from '../../hooks/useEmailAuth';
import NameField from '../common/NameField';

const ReviewModal = ({
  movie,
  isVisible,
  onClose,
  onSubmitReview
}) => {
  const [reviewForm, setReviewForm] = useState({
    reviewerName: '',
    overallRating: 0,
    scarinessRating: 0,
    currentTag: '',
    tags: []
  });

  // NameField fills reviewerName in from the signed-in profile; the modal only
  // has to hold whatever name it settles on.
  const { getRememberedName } = useEmailAuth();

  // Whether a save is in flight, and why the last one failed. A failed save
  // keeps the modal open with the ratings intact so it can simply be retried.
  const [submission, setSubmission] = useState({ busy: false, error: '' });

  // A stale error must not greet the next review.
  useEffect(() => {
    if (isVisible) setSubmission({ busy: false, error: '' });
  }, [isVisible]);

  const predefinedTags = [
    '"Amazing" acting',
    'I screamed',
    'Traumatic',
    'Sweet kills',
    'One great scene',
    'Hilarious',
    'Acid trip',
    'Dubious special effects',
    'Coffin surfing'
  ];

  if (!isVisible || !movie) return null;

  const getSelectedReviewName = () => reviewForm.reviewerName.trim();

  const addTagToReview = (tag) => {
    if (tag && !reviewForm.tags.includes(tag)) {
      setReviewForm(prev => ({
        ...prev,
        tags: [...prev.tags, tag],
        currentTag: ''
      }));
    }
  };

  const removeTagFromReview = (tagToRemove) => {
    setReviewForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async () => {
    const reviewName = getSelectedReviewName();
    if (!reviewName || submission.busy) return;

    const newReview = {
      name: reviewName,
      overallRating: reviewForm.overallRating,
      scarinessRating: reviewForm.scarinessRating,
      tags: [...reviewForm.tags],
      timestamp: new Date().toISOString()
    };

    // This used to fire-and-forget and close at once, so a save that failed
    // had nowhere to show itself. Now it waits for the answer. Promise.resolve
    // tolerates a caller that still returns nothing, which counts as success.
    setSubmission({ busy: true, error: '' });
    const result = await Promise.resolve(onSubmitReview(movie.id, newReview));

    if (result && result.ok === false) {
      setSubmission({ busy: false, error: result.reason || 'Your review did not save' });
      return; // form and ratings stay exactly as they were
    }

    setSubmission({ busy: false, error: '' });

    // Reset form. reviewerName is left for NameField to refill from the
    // profile, so a second review does not ask who you are all over again.
    setReviewForm({
      reviewerName: '',
      overallRating: 0,
      scarinessRating: 0,
      currentTag: '',
      tags: []
    });

    onClose();
  };

  const handleClose = () => {
    // Reset form on close
    setReviewForm({
      selectedName: '',
      customReviewName: '',
      overallRating: 0,
      scarinessRating: 0,
      currentTag: '',
      tags: []
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-6 py-4 border-b-2 border-orange-500">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-orange-100">📝 Leave a Review</h2>
            <button
              onClick={handleClose}
              className="bg-red-600 hover:bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* Movie Info */}
          <div className="flex items-center gap-3 mb-6 p-3 bg-gray-900 rounded-lg border border-orange-500">
            <div className={`w-12 h-18 ${movie.color} rounded border border-orange-600 flex items-center justify-center`}>
              <span className="text-white text-xs font-bold px-1 leading-tight text-center">
                {movie.title.split(' ').map(word => word[0]).join('').slice(0, 3)}
              </span>
            </div>
            <div>
              <h3 className="text-orange-100 font-bold">{movie.title}</h3>
              <p className="text-orange-300 text-sm">({movie.year})</p>
            </div>
          </div>

          {/* Name Selection */}
          <div className="mb-6">
            <h4 className="text-orange-300 font-bold mb-3">👤 Your Name</h4>
            
            <NameField
              name={reviewForm.reviewerName}
              onNameChange={(value) => setReviewForm(prev => ({ ...prev, reviewerName: value }))}
              profileName={getRememberedName()}
              action="Reviewing"
              accent="orange"
            />
          </div>

          {/* Overall Rating */}
          <div className="mb-6">
            <h4 className="text-orange-300 font-bold mb-3">⚰️ Overall Rating</h4>
            <div className="flex gap-2">
              {[0,1,2,3,4,5].map(rating => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => {
                    setReviewForm(prev => ({ ...prev, overallRating: rating }));
                  }}
                  className={`p-2 rounded-lg border-2 transition-all cursor-pointer hover:scale-110 ${
                    rating === reviewForm.overallRating
                      ? 'text-yellow-400 border-yellow-400 bg-yellow-900/20' 
                      : 'text-gray-600 border-gray-600 hover:text-yellow-300 hover:border-yellow-300'
                  }`}
                >
                  <span className="text-lg font-bold">{rating === 0 ? '0' : '⚰️'}</span>
                </button>
              ))}
            </div>
            <div className="mt-2 text-sm text-orange-400">
              Current rating: {reviewForm.overallRating}/5 {reviewForm.overallRating === 0 && '(No rating)'}
            </div>
          </div>

          {/* Scariness Rating */}
          <div className="mb-6">
            <h4 className="text-orange-300 font-bold mb-3">😱 Scariness Rating</h4>
            <div className="flex gap-2">
              {[0,1,2,3,4,5].map(rating => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => {
                    setReviewForm(prev => ({ ...prev, scarinessRating: rating }));
                  }}
                  className={`p-2 rounded-lg border-2 transition-all cursor-pointer hover:scale-110 ${
                    rating === reviewForm.scarinessRating
                      ? 'text-red-400 border-red-400 bg-red-900/20' 
                      : 'text-gray-600 border-gray-600 hover:text-red-300 hover:border-red-300'
                  }`}
                >
                  <span className="text-lg font-bold">{rating === 0 ? '0' : '😱'}</span>
                </button>
              ))}
            </div>
            <div className="mt-2 text-sm text-orange-400">
              Current scariness: {reviewForm.scarinessRating}/5 {reviewForm.scarinessRating === 0 && '(No rating)'}
            </div>
          </div>

          {/* Tags */}
          <div className="mb-6">
            <h4 className="text-orange-300 font-bold mb-3">🏷️ Tags</h4>
            
            {/* Custom Tag Input */}
            <div className="mb-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Add a custom tag..."
                  value={reviewForm.currentTag}
                  onChange={(e) => setReviewForm(prev => ({ ...prev, currentTag: e.target.value }))}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addTagToReview(reviewForm.currentTag);
                    }
                  }}
                  className="flex-1 bg-gray-700 border border-orange-500 text-white rounded px-3 py-2"
                />
                <button
                  onClick={() => addTagToReview(reviewForm.currentTag)}
                  disabled={!reviewForm.currentTag.trim()}
                  className="bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 text-white px-4 py-2 rounded font-bold transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
            
            {/* Predefined Tags */}
            <div className="mb-3">
              <p className="text-orange-200 text-sm mb-2">Quick tags:</p>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {predefinedTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => addTagToReview(tag)}
                    disabled={reviewForm.tags.includes(tag)}
                    className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:opacity-50 text-orange-200 px-2 py-1 rounded text-xs font-medium transition-colors"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Selected Tags */}
            {reviewForm.tags.length > 0 && (
              <div>
                <p className="text-orange-200 text-sm mb-2">Selected tags:</p>
                <div className="flex flex-wrap gap-2">
                  {reviewForm.tags.map(tag => (
                    <span
                      key={tag}
                      className="bg-yellow-700 text-yellow-100 px-2 py-1 rounded text-xs font-medium flex items-center gap-1"
                    >
                      {tag}
                      <button
                        onClick={() => removeTagFromReview(tag)}
                        className="text-yellow-200 hover:text-white ml-1"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          {submission.error && (
            <p role="alert" className="text-red-300 text-sm mb-3">
              ⚠️ {submission.error}. Your ratings are still here - try again.
            </p>
          )}
          <div className="flex gap-3 justify-end">
            <button
              onClick={handleClose}
              className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg font-bold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!getSelectedReviewName() || submission.busy}
              className="bg-yellow-600 hover:bg-yellow-500 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-bold transition-colors"
            >
              {submission.busy ? '⏳ Saving...' : '📝 Submit Review'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;

